import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Save, Send, CheckCircle2, Star, Printer } from 'lucide-react';
import { SignaturePad } from '@/components/forms/SignaturePad';
import { engineActive, engineUnsupported, resolveRules, validateSubmission } from '@/lib/formRules';

const STATUS_COLORS = {
  DRAFT: 'secondary', SUBMITTED: 'default', UNDER_REVIEW: 'default',
  APPROVED: 'default', REJECTED: 'destructive',
};

const fid = (key) => `fill-${key}`;

function FieldLabel({ field, required, as = 'label' }) {
  const Tag = as === 'span' ? 'span' : Label;
  const props = as === 'span' ? { id: `${fid(field.key)}-label` } : { htmlFor: fid(field.key), id: `${fid(field.key)}-label` };
  return <Tag className="text-sm" {...props}>{field.label}{required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}{required && <span className="sr-only"> (required)</span>}</Tag>;
}

function FieldError({ field, error }) {
  if (!error) return null;
  return <p id={`${fid(field.key)}-error`} className="text-xs text-red-600 mt-1" data-testid={`${fid(field.key)}-error`}>{error}</p>;
}

// meta = { required, error, readOnly } — required/visibility already resolved by the caller (rules engine v1) or static (legacy).
function renderField(field, value, onChange, meta) {
  const key = field.key; const { required, error, readOnly } = meta;
  const helpId = field.help_text ? `${fid(key)}-help` : null; const errId = error ? `${fid(key)}-error` : null;
  const describedBy = [helpId, errId].filter(Boolean).join(' ') || undefined;
  const common = { disabled: readOnly, id: fid(key), 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined, 'aria-required': required || undefined };
  const help = field.help_text ? <p id={helpId} className="text-xs text-muted-foreground mb-1">{field.help_text}</p> : null;
  const groupProps = (role) => ({ role, 'aria-labelledby': `${fid(key)}-label`, 'aria-describedby': describedBy, 'aria-required': required || undefined, 'aria-invalid': error ? true : undefined, 'data-testid': fid(key) });

  switch (field.type) {
    case 'section_header':
      return (
        <div className="pt-4 pb-1">
          <h2 className="text-base font-semibold text-foreground">{field.label}</h2>
          <Separator className="mt-2" />
        </div>
      );
    case 'divider':
      return <Separator className="my-2" />;
    case 'short_text':
    case 'email':
    case 'phone':
      return (
        <div>
          <FieldLabel field={field} required={required} />{help}
          <Input type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'} value={value || ''} onChange={e => onChange(key, e.target.value)} placeholder={field.placeholder || ''} {...common} data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
    case 'long_text':
      return (
        <div>
          <FieldLabel field={field} required={required} />{help}
          <Textarea rows={4} value={value || ''} onChange={e => onChange(key, e.target.value)} placeholder={field.placeholder || ''} {...common} data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
    case 'number':
      return (
        <div>
          <FieldLabel field={field} required={required} />{help}
          <Input type="number" value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
    case 'date':
      return (
        <div>
          <FieldLabel field={field} required={required} />{help}
          <Input type="date" value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
    case 'time':
      return (
        <div>
          <FieldLabel field={field} required={required} />
          <Input type="time" value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
    case 'dropdown':
      return (
        <div>
          <FieldLabel field={field} required={required} />{help}
          <Select value={value || ''} onValueChange={v => onChange(key, v)} disabled={readOnly}>
            <SelectTrigger id={fid(key)} aria-describedby={describedBy} aria-invalid={error ? true : undefined} aria-required={required || undefined} data-testid={fid(key)}><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          <FieldError field={field} error={error} />
        </div>
      );
    case 'multi_select':
    case 'checkbox': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div>
          <FieldLabel field={field} required={required} as="span" />{help}
          <div className="flex flex-wrap gap-2 mt-1" {...groupProps('group')}>
            {(field.options || []).map(o => {
              const checked = selected.includes(o);
              return (
                <Button key={o} type="button" role="checkbox" aria-checked={checked} variant={checked ? 'default' : 'outline'} size="sm" className="h-8 text-xs"
                  disabled={readOnly}
                  onClick={() => {
                    if (checked) onChange(key, selected.filter(x => x !== o));
                    else onChange(key, [...selected, o]);
                  }}
                  data-testid={`${fid(key)}-${o}`}
                >{o}</Button>
              );
            })}
          </div>
          <FieldError field={field} error={error} />
        </div>
      );
    }
    case 'radio':
      return (
        <div>
          <FieldLabel field={field} required={required} as="span" />
          <div className="flex flex-wrap gap-2 mt-1" {...groupProps('radiogroup')}>
            {(field.options || []).map(o => (
              <Button key={o} type="button" role="radio" aria-checked={value === o} variant={value === o ? 'default' : 'outline'} size="sm" className="h-8 text-xs"
                disabled={readOnly} onClick={() => onChange(key, o)} data-testid={`${fid(key)}-${o}`}
              >{o}</Button>
            ))}
          </div>
          <FieldError field={field} error={error} />
        </div>
      );
    case 'yes_no':
      return (
        <div className="py-1">
          <div className="flex items-center justify-between">
            <div>
              <FieldLabel field={field} required={required} as="span" />
              {field.help_text && <p id={helpId} className="text-xs text-muted-foreground">{field.help_text}</p>}
            </div>
            <div className="flex items-center gap-2" {...groupProps('radiogroup')}>
              <Button type="button" role="radio" aria-checked={value === 'Yes'} variant={value === 'Yes' ? 'default' : 'outline'} size="sm" className="h-7 text-xs px-3"
                disabled={readOnly} onClick={() => onChange(key, 'Yes')} data-testid={`${fid(key)}-yes`}
              >Yes</Button>
              <Button type="button" role="radio" aria-checked={value === 'No'} variant={value === 'No' ? 'destructive' : 'outline'} size="sm" className="h-7 text-xs px-3"
                disabled={readOnly} onClick={() => onChange(key, 'No')} data-testid={`${fid(key)}-no`}
              >No</Button>
            </div>
          </div>
          <FieldError field={field} error={error} />
        </div>
      );
    case 'rating': {
      const stars = [1, 2, 3, 4, 5];
      const current = parseInt(value) || 0;
      return (
        <div>
          <FieldLabel field={field} required={required} as="span" />
          <div className="flex gap-1 mt-1" {...groupProps('radiogroup')}>
            {stars.map(s => (
              <button key={s} type="button" role="radio" aria-checked={current === s} aria-label={`${s} of 5`} disabled={readOnly}
                className={`p-1 ${s <= current ? 'text-yellow-500' : 'text-muted-foreground/30'}`}
                onClick={() => onChange(key, s.toString())}
              ><Star className="w-6 h-6" fill={s <= current ? 'currentColor' : 'none'} /></button>
            ))}
          </div>
          <FieldError field={field} error={error} />
        </div>
      );
    }
    case 'address':
      return (
        <div>
          <FieldLabel field={field} required={required} />
          <Textarea rows={2} value={value || ''} onChange={e => onChange(key, e.target.value)} placeholder="Full address..." {...common} data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
    case 'file_upload':
    case 'image_upload':
      return (
        <div>
          <FieldLabel field={field} required={required} />
          <Input type="file" {...common} className="text-sm" data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
    case 'signature':
      return (
        <div>
          <FieldLabel field={field} required={required} />
          <div className="border rounded-md p-4 bg-muted/20 text-center text-sm text-muted-foreground">
            {value ? <p className="font-medium text-foreground">Signed: {value}</p> : (
              <Input placeholder="Type your full name as signature" value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={fid(key)} />
            )}
          </div>
          <FieldError field={field} error={error} />
        </div>
      );
    case 'participant_lookup':
    case 'staff_lookup':
    case 'shift_lookup':
      return (
        <div>
          <FieldLabel field={field} required={required} />
          <Input value={value || ''} onChange={e => onChange(key, e.target.value)} placeholder={`Enter ${field.type.replace('_lookup', '')} ID or name`} {...common} data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
    default:
      return (
        <div>
          <FieldLabel field={field} required={false} />
          <Input value={value || ''} onChange={e => onChange(key, e.target.value)} {...common} data-testid={fid(key)} />
          <FieldError field={field} error={error} />
        </div>
      );
  }
}

function ErrorSummary({ errors, schema }) {
  const ref = useRef(null);
  useEffect(() => { if (errors.length) ref.current?.focus(); }, [errors]);
  if (!errors.length) return null;
  const label = (k) => schema.find(f => f.key === k)?.label || k;
  const focusField = (e, key) => {
    e.preventDefault();
    const el = document.getElementById(fid(key)) || document.querySelector(`[data-testid="${fid(key)}"]`);
    const target = el && el.getAttribute('role') && !['input', 'textarea', 'button'].includes(el.tagName.toLowerCase()) ? (el.querySelector('button, input, [tabindex]') || el) : el;
    target?.focus(); target?.scrollIntoView?.({ block: 'center' });
  };
  return (
    <Card className="border-red-200 bg-red-50" ref={ref} tabIndex={-1} role="alert" aria-labelledby="fill-error-summary-title" data-testid="fill-error-summary">
      <CardContent className="py-3">
        <p id="fill-error-summary-title" className="text-sm font-semibold text-red-800">Please fix {errors.length} {errors.length === 1 ? 'problem' : 'problems'} before submitting</p>
        <ul className="mt-1 list-disc pl-5 text-sm text-red-800">
          {errors.map(e => (
            <li key={`${e.field}-${e.code}`}><a href={`#${fid(e.field)}`} className="underline" onClick={ev => focusField(ev, e.field)} data-testid={`fill-error-link-${e.field}`}>{label(e.field)}: {e.message}</a></li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function FormFillPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [schema, setSchema] = useState([]);
  const [version, setVersion] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [submission, setSubmission] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, sRes] = await Promise.all([
          api.get(`/forms/assignments/${assignmentId}`),
          api.get(`/forms/assignments/${assignmentId}/submission`),
        ]);
        const a = aRes.data.data;
        setAssignment(a);
        setTemplateName(a.template?.name || 'Form');
        setSchema(a.version?.schema_json || []);
        setVersion(a.version || null);
        if (sRes.data.data) {
          setSubmission(sRes.data.data);
          setFormData(sRes.data.data.data_json || {});
        }
      } catch { toast.error('Failed to load form'); }
      setLoading(false);
    };
    load();
  }, [assignmentId]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setErrors(prev => prev.filter(e => e.field !== key));
  };

  // Rules engine v1 runs only when the pinned version carries the marker; legacy versions render exactly as before.
  const rulesActive = engineActive(version);
  const rulesUnsupported = engineUnsupported(version);
  const rules = version?.rules_json || [];
  const resolved = useMemo(() => rulesActive ? resolveRules(schema, rules, formData) : null, [rulesActive, schema, rules, formData]);
  const isHidden = (key) => !!resolved?.hidden.has(key);
  const isRequired = (field) => rulesActive ? resolved.required.has(field.key) : !!field.required;

  const isReadOnly = submission && ['APPROVED', 'LOCKED', 'SUBMITTED', 'UNDER_REVIEW'].includes(submission.status);
  const [signatures, setSignatures] = useState({});
  useEffect(() => {
    if (!submission?.id) return;
    api.get(`/forms/submissions/${submission.id}/signatures`).then(({ data }) => {
      const m = {}; (data.items || []).forEach(sg => { m[sg.field_key] = sg; }); setSignatures(m);
    }).catch(() => {});
  }, [submission?.id]);
  const uploadSignature = async (field, cap) => {
    const fd = new FormData();
    const existing = signatures[field.key];
    // Only capacity/contact/assistance/attestation-ack are client-supplied; identity, wording and timestamps are server-derived.
    fd.append('meta', JSON.stringify({
      field_key: field.key, signer_capacity: cap.signerCapacity, attestation_version_ack: field.attestation_version,
      idempotency_key: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/-/g, ''),
      signer_contact_id: cap.signerContactId || null, assisted_by_me: !!cap.assistedByMe, assistance_note: cap.assistanceNote || null,
      replaces_signature_id: existing?.replacing ? existing.id : null, replacement_reason: existing?.replacing ? cap.replacementReason : null,
    }));
    fd.append('image', cap.blob, 'signature.png');
    try {
      const { data } = await api.post(`/forms/submissions/${submission.id}/signatures`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSignatures(s => ({ ...s, [field.key]: data.data })); toast.success(existing?.replacing ? 'Signature replaced' : 'Signature saved');
    } catch (e) {
      const err = e.response?.data?.error;
      toast.error(err?.message || 'Signature could not be saved');
      if (err?.code === 'SIGNATURE_CONFLICT' || err?.code === 'SUBMISSION_LOCKED') {
        const { data } = await api.get(`/forms/submissions/${submission.id}/signatures`).catch(() => ({ data: { items: [] } }));
        const m = {}; (data.items || []).forEach(sg => { m[sg.field_key] = sg; }); setSignatures(m);
      }
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      if (submission) {
        await api.put(`/forms/submissions/${submission.id}/draft`, { data_json: formData });
        toast.success('Draft saved');
      } else {
        const { data } = await api.post('/forms/submissions', { assignment_id: assignmentId, data_json: formData });
        setSubmission(data.data);
        toast.success('Draft created');
      }
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const clientValidate = () => {
    if (rulesActive) {
      const drawn = new Set(schema.filter(f => f.type === 'signature_drawn').map(f => f.key));
      return validateSubmission(schema, rules, formData, drawn).errors.filter(e => e.code !== 'UNKNOWN_FIELD');
    }
    // Legacy: unchanged static-required check.
    return schema.filter(f => f.required && f.key && !['section_header', 'divider'].includes(f.type)).filter(f => {
      const v = formData[f.key];
      return !v || (Array.isArray(v) && v.length === 0);
    }).map(f => ({ field: f.key, code: 'REQUIRED_MISSING', message: `${f.label} is required.` }));
  };

  const handleSubmit = async () => {
    if (rulesUnsupported) { toast.error('This form version uses a newer rules engine than this application supports.'); return; }
    const missing = clientValidate();
    if (missing.length > 0) {
      setErrors(missing);
      toast.error(`Please fill required fields: ${missing.map(e => schema.find(f => f.key === e.field)?.label || e.field).join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      let subId = submission?.id;
      // Drafts keep every answer (including hidden ones); the server strips hidden values atomically at submit.
      if (!subId) {
        const { data } = await api.post('/forms/submissions', { assignment_id: assignmentId, data_json: formData });
        subId = data.data.id;
        setSubmission(data.data);
      } else {
        await api.put(`/forms/submissions/${subId}/draft`, { data_json: formData });
      }
      const { data: result } = await api.post(`/forms/submissions/${subId}/submit`);
      setSubmission(prev => ({ ...prev, status: result.data.status }));
      setAssignment(prev => ({ ...prev, status: 'SUBMITTED' }));
      setErrors([]);
      toast.success('Form submitted successfully!');
    } catch (e) {
      const err = e.response?.data?.error;
      if (err?.code === 'SUBMISSION_INVALID' && Array.isArray(err.details?.errors)) setErrors(err.details.errors);
      toast.error(err?.message || e.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground">Loading form...</div>;
  if (!assignment) return <div className="text-center py-20 text-muted-foreground">Assignment not found</div>;

  const assignmentStatus = assignment.status;
  const submissionStatus = submission?.status;
  const errorFor = (key) => errors.find(e => e.field === key)?.message;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="form-fill-page" data-rules-engine={rulesActive ? version.rules_engine_version : 'legacy'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{templateName}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant={STATUS_COLORS[submissionStatus || assignmentStatus] || 'secondary'}>
                {submissionStatus || assignmentStatus}
              </Badge>
              {assignment.due_date && <span className="text-xs text-muted-foreground">Due: {assignment.due_date}</span>}
            </div>
          </div>
        </div>
        {!isReadOnly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/forms/assignments/${assignmentId}/print`)} data-testid="print-form-btn">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-form-btn">
              <Send className="w-4 h-4 mr-2" />{submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        )}
        {isReadOnly && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/forms/assignments/${assignmentId}/print`)} data-testid="print-form-btn">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
          </div>
        )}
      </div>
      {isReadOnly && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-3 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-800 font-medium">
              This form has been {submissionStatus?.toLowerCase()}. It is now read-only.
            </p>
          </CardContent>
        </Card>
      )}
      {rulesUnsupported && (
        <Card className="border-amber-200 bg-amber-50" role="alert" data-testid="fill-rules-unsupported">
          <CardContent className="py-3"><p className="text-sm text-amber-800 font-medium">This form version uses a newer rules engine than this application supports. It cannot be submitted here.</p></CardContent>
        </Card>
      )}

      {submission?.rejection_reason && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3">
            <p className="text-sm text-red-800 font-medium">Rejected: {submission.rejection_reason}</p>
          </CardContent>
        </Card>
      )}

      <ErrorSummary errors={errors} schema={schema} />

      {/* Form Fields — hidden (condition not met) fields are unmounted, so they leave the tab order entirely. */}
      <Card>
        <CardContent className="py-6 space-y-5">
          {schema.filter(field => !isHidden(field.key)).map((field, i) => (
            <div key={field.key || `field-${i}`} data-testid={`fill-field-${field.key}`}>
              {field.type === 'signature_drawn' ? (
                <div>
                  <Label className="text-sm">{field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}</Label>
                  {!submission ? <p className="text-xs text-muted-foreground">Save a draft first to enable signing.</p> : (
                    <SignaturePad fieldKey={field.key} label={field.label} disabled={isReadOnly || submission.status !== 'DRAFT'}
                      attestationText={field.attestation_text} attestationVersion={field.attestation_version}
                      participantId={assignment?.target_type === 'PARTICIPANT' ? assignment.target_id : null}
                      existing={signatures[field.key] || null}
                      onReplace={() => setSignatures(s => ({ ...s, [field.key]: { ...s[field.key], replacing: true } }))}
                      onCancelReplace={() => setSignatures(s => ({ ...s, [field.key]: { ...s[field.key], replacing: false } }))}
                      onCapture={(cap) => uploadSignature(field, cap)} />
                  )}
                  {submission?.status === 'REJECTED' && <p className="text-xs text-amber-700" data-testid={`signature-redraft-hint-${field.key}`}>Save the draft to reopen this rejected form before changing signatures.</p>}
                </div>
              ) : renderField(field, formData[field.key], handleChange, { required: isRequired(field), error: errorFor(field.key), readOnly: isReadOnly })}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Bottom Actions */}
      {!isReadOnly && (
        <div className="flex items-center justify-end gap-2 pb-8">
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn-bottom">
            <Save className="w-4 h-4 mr-2" />Save Draft
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-form-btn-bottom">
            <Send className="w-4 h-4 mr-2" />Submit Form
          </Button>
        </div>
      )}
    </div>
  );
}
